import { z } from 'zod';

import { BUSINESS_WIRE, PR_NEWSWIRE, PUBT, REUTERS } from './news';
import {
  scenariosRequestConditionSchema,
  scenariosRequestEventSchema,
} from './scenario';

const inventoryEntityItem = z.object({
  tag: z.string(),
});

const inventoryTimeseriesItem = z.object({
  snake: z.string(),
});

const inventoryWebLinkItem = z.object({
  urls: z.array(z.string()).nullish(),
});

const inventoryExternalDataItem = z.object({
  links: z.array(z.string()).nullish(),
});

const inventoryArtifactItem = z.object({
  artifact_id: z.string(),
  provenance_schema_version: z.string().optional(),
});

const inventoryInsightItem = z.object({
  id: z.string(),
});

const isoDateString = z
  .string()
  .refine(val => !isNaN(new Date(val).getTime()), {
    message: 'Invalid date string',
  });

const inventoryEconomicCalendarItem = z.object({
  categories: z.array(z.string()).nullish(),
  countries: z.array(z.string()).nullish(),
  from: isoDateString,
  to: isoDateString,
});

export type InventoryEconomicCalendarItem = z.infer<
  typeof inventoryEconomicCalendarItem
>;

const inventoryCorporateActionItem = z.object({
  id: z.string().uuid().optional(),
  provider_event_id: z.string(),
  tag: z.string(),
  action_type: z.string(),
  effective_date: isoDateString,
});

export type InventoryCorporateActionItem = z.infer<
  typeof inventoryCorporateActionItem
>;

const inventoryEarningsCalendarItem = z.object({
  entities: z.array(z.string()).nullish(),
  from: isoDateString.nullish(),
  to: isoDateString.nullish(),
});

export type InventoryEarningsCalendarItem = z.infer<
  typeof inventoryEarningsCalendarItem
>;

const inventoryEarningsHistoryItem = z.object({
  entity_tag: z.string(),
  from: isoDateString.nullish(),
  to: isoDateString.nullish(),
});

export type InventoryEarningsHistoryItem = z.infer<
  typeof inventoryEarningsHistoryItem
>;

const inventoryIpoEventItem = z.object({
  exchange: z.string(),
  name: z.string(),
  tag: z.string(),
  ticker: z.string(),
});

export type InventoryIpoEventItem = z.infer<typeof inventoryIpoEventItem>;

const financialStatementsItem = z.object({
  tag: z.string(),
});

const documentSchema = z.object({
  Authority: z.number(),
  Country: z.string(),
  Entity: z.string(),
  ID: z.string(),
  Kind: z.string(),
  Rank: z.number(),
});

const sourceSchema = z.union([
  z.literal(REUTERS),
  z.literal(PUBT),
  z.literal(PR_NEWSWIRE),
  z.literal(BUSINESS_WIRE),
]);

const inventoryNewsItem = z.object({
  article_id: z.string(),
  title: z.string(),
  source: sourceSchema,
  published: z.string(),
  version: z.number().nullish(),
});

export type DocumentSearchSchema = z.infer<typeof documentSchema>;

const documentSearchItem = z.object({
  question: z.string().optional(),
  citation_groups: z.array(z.string()).nullish(),
  documents: z.array(documentSchema).nullish(),
});

export type DocumentSearchItem = z.infer<typeof documentSearchItem>;

/** Anchor type for KG inventory rows. */
export const inventoryKgNodeType = z.enum(['company', 'theme']);

export type InventoryKgNodeType = z.infer<typeof inventoryKgNodeType>;
export type InventoryNewsItem = z.infer<typeof sourceSchema>;
/** Atomic relation (paired with `node_type` + `node_key`). */
export const inventoryKgRelationType = z.enum([
  'theme',
  'company',
  'product',
  'country',
  'region',
  'competitor',
]);

export type InventoryKgRelationType = z.infer<typeof inventoryKgRelationType>;

export const inventoryKgFilterItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
});

export type InventoryKgFilterItem = z.infer<typeof inventoryKgFilterItemSchema>;

export const inventoryKgItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
  relation_type: inventoryKgRelationType,
  filter: z.array(inventoryKgFilterItemSchema).optional(),
});

export type InventoryKgItem = z.infer<typeof inventoryKgItemSchema>;

export const conversationInventoryKnowledgeGraphKey =
  'knowledge-graph' as const;

const inventoryScenarioAnalysisItem = z.object({
  description: z.string(),
  benchmark: z.string().optional(),
  entities: z.number(),
  event_mode: z.string().optional(),
  n_events: z.number().optional(),
  tool: z.string().optional(),
  entity: z.string().optional(),
  conditions: z.array(scenariosRequestConditionSchema).optional(),
  events: z.array(scenariosRequestEventSchema).optional(),
  entity_tags: z.array(z.string()).optional(),
  non_overlapping: z.boolean().optional(),
});

export type InventoryScenarioAnalysisItem = z.infer<
  typeof inventoryScenarioAnalysisItem
>;

const conversationInventorySchema = z.object({
  entity: z.object({ items: z.array(inventoryEntityItem) }).optional(),
  timeseries: z.object({ items: z.array(inventoryTimeseriesItem) }).optional(),
  artifact: z.object({ items: z.array(inventoryArtifactItem) }).optional(),
  insight: z.object({ items: z.array(inventoryInsightItem) }).optional(),
  news: z.object({ items: z.array(inventoryNewsItem) }).optional(),
  ['web-links']: z.object({ items: z.array(inventoryWebLinkItem) }).optional(),
  ['external-data']: z
    .object({ items: z.array(inventoryExternalDataItem) })
    .optional(),
  ['financial-statement']: z
    .object({ items: z.array(financialStatementsItem) })
    .optional(),
  ['document-search']: z
    .object({ items: z.array(documentSearchItem) })
    .optional(),
  [conversationInventoryKnowledgeGraphKey]: z
    .object({ items: z.array(inventoryKgItemSchema) })
    .optional(),
  ['economic-calendar']: z
    .object({ items: z.array(inventoryEconomicCalendarItem) })
    .optional(),
  ['corporate-action']: z
    .object({ items: z.array(inventoryCorporateActionItem) })
    .optional(),
  ['earnings-calendar']: z
    .object({ items: z.array(inventoryEarningsCalendarItem) })
    .optional(),
  ['earnings-history']: z
    .object({ items: z.array(inventoryEarningsHistoryItem) })
    .optional(),
  ['ipo-event']: z.object({ items: z.array(inventoryIpoEventItem) }).optional(),
  analysis: z
    .object({ items: z.array(inventoryScenarioAnalysisItem) })
    .optional(),
});

export const inventory = {
  conversationInventory: {
    path: (conversationId: string) =>
      `conversation-inventory/v1/conversations/${conversationId}`,
    schema: conversationInventorySchema,
  },
};
